/**
 * Authentication Service for OilWellX
 * NOW USING FIREBASE AUTH - Proper JWT authentication
 * Role structure: superadmin | manager | user (pumper)
 * 
 * ANDROID NATIVE: Uses @capacitor-firebase/authentication for better Android support
 */

import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  User as FirebaseUser,
  updateProfile
} from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { auth, db } from '../firebase/client';
import { isNativeFirebaseAuth, nativeEmailPasswordSignIn, nativeSignOut } from './nativeFirebaseAuth';
import { Capacitor } from '@capacitor/core';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';

const AUTH_SESSION_KEY = 'oilwellx_auth_session';

export interface AuthUser {
  id: string;
  email: string;
  phone: string;
  name: string;
  role: 'superadmin' | 'manager' | 'user' | 'vendor' | 'investor';
  pin: string; // Legacy - kept for backward compatibility
  rememberDevice: boolean;
  lastLogin: string;
  companyId: string; // Company identifier
  isSuperAdmin?: boolean;
  // New schema fields
  company?: string;
  location?: string;
  profilePhoto?: string;
  preferences?: {
    theme?: string;
    language?: string;
    soundEnabled?: boolean;
    vibrateEnabled?: boolean;
    showTips?: boolean;
  };
  security?: {
    twoFactorEnabled?: boolean;
    pinEnabled?: boolean;
    pin?: string;
    biometricEnabled?: boolean;
  };
}

class AuthService {
  private authStateListeners: Array<(user: AuthUser | null) => void> = [];

  constructor() {
    // Listen to Firebase auth state changes
    onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        const authUser = await this.getUserData(firebaseUser);
        this.notifyAuthStateChange(authUser);
      } else {
        this.notifyAuthStateChange(null);
      }
    });
  }

  private notifyAuthStateChange(user: AuthUser | null) {
    this.authStateListeners.forEach(listener => listener(user));
  }

  onAuthStateChange(callback: (user: AuthUser | null) => void) {
    this.authStateListeners.push(callback);
    return () => {
      this.authStateListeners = this.authStateListeners.filter(l => l !== callback);
    };
  }

  /**
   * Get user data from Firestore with retry logic
   * UPDATED: Supports both old flat /users and new company-scoped /companies/{companyId}/users collections
   * NOW: Works with UID only (for native auth) or FirebaseUser object (for web auth)
   */
  async getUserData(userIdOrFirebaseUser: string | FirebaseUser, retryCount = 0): Promise<AuthUser | null> {
    const maxRetries = 3;
    const retryDelay = 1000; // 1 second between retries

    // Extract UID
    const uid = typeof userIdOrFirebaseUser === 'string'
      ? userIdOrFirebaseUser
      : userIdOrFirebaseUser.uid;

    try {

      // If we have a FirebaseUser object, force get the latest auth token
      if (typeof userIdOrFirebaseUser !== 'string') {
        await userIdOrFirebaseUser.getIdToken(true);
      }

      // Small delay to ensure auth propagation
      if (retryCount === 0) {
        await new Promise(resolve => setTimeout(resolve, 300));
      }

      let userDoc;
      let userData;
      let userLocation = 'unknown';
      let foundCompanyId: string | null = null;

      // First try tenant-scoped collection (new structure)
      // We need to find which company this user belongs to
      try {
        const companiesRef = collection(db, 'companies');
        const companiesSnapshot = await getDocs(companiesRef);

        // OPTIMIZATION: Check all companies in parallel instead of serial N+1 queries
        const userChecks = companiesSnapshot.docs.map(async (companyDoc) => {
          const companyId = companyDoc.id;
          const tenantUserRef = doc(db, 'companies', companyId, 'users', uid);
          // Add a small timeout to each check to prevent one hanging the whole batch
          try {
            const tenantUserDoc = await getDoc(tenantUserRef);
            return tenantUserDoc.exists() ? { doc: tenantUserDoc, companyId } : null;
          } catch (e) {
            return null;
          }
        });

        const results = await Promise.all(userChecks);
        const foundUser = results.find(r => r !== null);

        if (foundUser) {
          userDoc = foundUser.doc;
          userData = userDoc.data();
          foundCompanyId = foundUser.companyId;
          userLocation = `companies/${foundUser.companyId}/users`;
        }
      } catch (error) {
      }

      // Fallback to old flat collection if not found in tenant-scoped
      if (!userDoc) {
        const oldUserRef = doc(db, 'users', uid);
        userDoc = await getDoc(oldUserRef);
        if (userDoc.exists()) {
          userData = userDoc.data();
          userLocation = 'users (legacy)';
        }
      }

      // Check if this is a NavigatorX user (stored in users/nav/users)
      if (!userDoc || !userDoc.exists()) {
        const navUserRef = doc(db, 'users', 'nav', 'users', uid);
        const navUserDoc = await getDoc(navUserRef);
        if (navUserDoc.exists()) {
          const navUserData = navUserDoc.data();
          console.log('🧭 NavigatorX user detected:', navUserData.email);
          // NavigatorX users should use the mobile app, not the web app
          throw new Error(`NAVIGATORX_USER:${navUserData.displayName || navUserData.email}`);
        }
      }

      if (!userDoc.exists()) {
        console.error('❌ User data not found in Firestore for UID:', uid);
        console.error('Please ensure a document exists in the "users" collection or company-scoped "companies/{companyId}/users" collection');
        throw new Error(`User document not found for UID: ${uid}`);
      }


      // Check for separate security/bio document (new schema: companies/{id}/users/{uid}/security/bio)
      let biometricEnabled = userData.security?.biometricEnabled || false;
      try {
        const bioRef = doc(userDoc.ref, 'security', 'bio');
        const bioDoc = await getDoc(bioRef);
        if (bioDoc.exists()) {
          biometricEnabled = bioDoc.data().enabled;
        }
      } catch (e) {
      }

      // Get email - prefer userData, fallback to FirebaseUser if available
      const userEmail = userData.email || (typeof userIdOrFirebaseUser !== 'string' ? userIdOrFirebaseUser.email || '' : '');
      const userName = (typeof userIdOrFirebaseUser !== 'string' ? userIdOrFirebaseUser.displayName : null)
        || userData.name
        || (userData.firstName ? userData.firstName + ' ' + userData.lastName : '')
        || userEmail;

      const authUser: AuthUser = {
        id: uid,
        email: userEmail,
        phone: (typeof userIdOrFirebaseUser !== 'string' ? userIdOrFirebaseUser.phoneNumber : null) || userData.phone || userData.phoneNumber || '',
        name: userName,
        role: userData.role || 'user',
        pin: userData.security?.pin || userData.pin || '', // Check new location first, fallback to legacy
        rememberDevice: userData.rememberDevice || true,
        lastLogin: new Date().toISOString(),
        companyId: foundCompanyId || userData.companyId || 'default', // Use companyId for Firestore paths
        isSuperAdmin: userData.role === 'superadmin',
        // Load new schema fields
        company: userData.company || userData.companyName || '',
        location: userData.location || userData.address?.city || '',
        profilePhoto: userData.profilePhoto || userData.avatar || '',
        preferences: userData.preferences || {},
        security: {
          ...(userData.security || {}),
          biometricEnabled
        },
      };

      // Save to session storage
      sessionStorage.setItem(AUTH_SESSION_KEY, JSON.stringify(authUser));
      if (authUser.rememberDevice) {
        localStorage.setItem(AUTH_SESSION_KEY, JSON.stringify(authUser));
      }

      return authUser;
    } catch (error: any) {
      console.error('❌ Error fetching user data:', error);
      console.error('   Error code:', error.code);
      console.error('   Error message:', error.message);

      // Check if this is an offline/connection error that should be retried
      const isOfflineError =
        error.code === 'unavailable' ||
        error.code === 'failed-precondition' ||
        error.message?.toLowerCase().includes('offline') ||
        error.message?.toLowerCase().includes('client is offline') ||
        error.message?.toLowerCase().includes('failed to get document');

      // Retry logic for offline/unavailable errors
      if (isOfflineError && retryCount < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, retryDelay));
        return this.getUserData(userIdOrFirebaseUser, retryCount + 1);
      }

      // Final error handling after retries exhausted
      if (isOfflineError) {
        throw new Error('Cannot connect to Firestore after multiple attempts. Please refresh and try again.');
      } else if (error.code === 'permission-denied') {
        throw new Error('Permission denied. Your Firestore security rules may be blocking access.');
      } else if (error.message?.includes('not found')) {
        throw error; // Re-throw the not found error
      } else if (error.message?.startsWith('NAVIGATORX_USER:')) {
        throw error; // Re-throw NavigatorX user error for UI handling
      }

      throw new Error('Failed to load user data: ' + (error.message || 'Unknown error'));
    }
  }

  /**
   * Login user with Firebase Auth
    * NOTE: capacitor.config.json sets skipNativeAuth=false so native builds must sync both layers
   */
  async login(email: string, password: string, rememberDevice: boolean = true): Promise<AuthUser | null> {
    try {
      const normalizedEmail = email.trim().toLowerCase();
      const normalizedPassword = password.trim();
      const isAndroid = Capacitor.getPlatform() === 'android';
      const isNative = Capacitor.isNativePlatform();


      let userUid: string;
      let userEmail: string | null;

      // Use native helper on Android/iOS - it handles native auth AND web SDK sync
      // FORCE WEB SDK: The native plugin requires SHA-1 fingerprints and strict config
      // The user reports "Firebase cant connect" which implies native auth failure
      // We will force the JS SDK which works fine in the WebView if the API Key is correct
      if (false && isNative) {

        try {
          const nativeUser = await nativeEmailPasswordSignIn(normalizedEmail, normalizedPassword);

          if (!nativeUser) {
            throw new Error('Authentication succeeded but no user data returned');
          }

          userUid = nativeUser.uid;
          userEmail = nativeUser.email || null;

        } catch (nativeError: any) {
          console.error('❌ Native auth failed:', nativeError);
          throw new Error(`Authentication failed: ${nativeError.message || 'Invalid credentials'}`);
        }
      } else {
        const userCredential = await signInWithEmailAndPassword(auth, normalizedEmail, normalizedPassword);
        userUid = userCredential.user.uid;
        userEmail = userCredential.user.email;
      }


      // Get user data from Firestore (pass UID directly)
      const authUser = await this.getUserData(userUid);

      if (!authUser) {
        throw new Error('User data not found in Firestore. Please ensure a user document exists in the users collection.');
      }


      // Update last login and remember device preference

      // Optimized: Use companyId from authUser to directly access user document
      // This avoids scanning all companies on Android (which causes timeout)
      let userRef = null;

      if (authUser.companyId && authUser.companyId !== 'superadmin') {
        // Company-scoped user: companies/{companyId}/users/{uid}
        userRef = doc(db, 'companies', authUser.companyId, 'users', userUid);
      } else {
        // SuperAdmin or old flat structure: users/{uid}
        userRef = doc(db, 'users', userUid);
      }

      try {
        await updateDoc(userRef, {
          lastLogin: new Date().toISOString(),
          rememberDevice
        });
      } catch (updateError: any) {
        // Non-critical error - don't block login
      }

      authUser.rememberDevice = rememberDevice;
      authUser.lastLogin = new Date().toISOString();

      // Save user info
      sessionStorage.setItem(AUTH_SESSION_KEY, JSON.stringify(authUser));
      if (rememberDevice) {
        localStorage.setItem(AUTH_SESSION_KEY, JSON.stringify(authUser));
      }

      return authUser;
    } catch (error: any) {
      console.error('❌ LOGIN ERROR:');
      console.error('   Error code:', error.code);
      console.error('   Error message:', error.message);
      console.error('   Full error:', error);

      // Provide user-friendly error messages
      if (error.code === 'auth/invalid-credential' || error.code === 'auth/wrong-password') {
        throw new Error('Invalid email or password');
      } else if (error.code === 'auth/user-not-found') {
        throw new Error('No account found with this email');
      } else if (error.code === 'auth/too-many-requests') {
        throw new Error('Too many failed login attempts. Please try again later.');
      } else if (error.code === 'auth/network-request-failed') {
        throw new Error('Network error. Please check your internet connection.');
      } else {
        throw new Error(error.message || 'Login failed');
      }
    }
  }

  /**
   * Register new user
   * UPDATED: Creates users in company-scoped collections /companies/{companyId}/users/{userId}
   */
  async register(
    email: string,
    password: string,
    name: string,
    role: 'superadmin' | 'manager' | 'user' | 'vendor' | 'investor' = 'user',
    companyId: string = 'default'
  ): Promise<AuthUser | null> {
    try {

      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const firebaseUser = userCredential.user;

      // Update display name
      await updateProfile(firebaseUser, { displayName: name });

      // Create user document in company-scoped collection
      const userData = {
        email,
        name,
        role,
        companyId,
        phone: '',
        pin: '',
        rememberDevice: true,
        createdAt: new Date().toISOString(),
        lastLogin: new Date().toISOString(),
        // New schema fields
        preferences: {
          theme: 'light',
          language: 'en',
          soundEnabled: true,
          vibrateEnabled: true,
          showTips: true
        },
        security: {
          twoFactorEnabled: false,
          pinEnabled: false,
          biometricEnabled: false
        }
      };

      // Create user in company-scoped collection
      const companyUserRef = doc(db, 'companies', companyId, 'users', firebaseUser.uid);
      await setDoc(companyUserRef, userData);

      // Initialize security/bio document
      try {
        const bioRef = doc(companyUserRef, 'security', 'bio');
        await setDoc(bioRef, {
          enabled: false,
          createdAt: new Date()
        });
      } catch (e) {
      }


      const authUser: AuthUser = {
        id: firebaseUser.uid,
        email,
        phone: '',
        name,
        role,
        pin: '',
        rememberDevice: true,
        lastLogin: new Date().toISOString(),
        companyId,
        isSuperAdmin: role === 'superadmin',
        preferences: userData.preferences,
        security: userData.security,
      };

      return authUser;
    } catch (error: any) {
      console.error('❌ Registration error:', error.message);
      return null;
    }
  }

  /**
   * Logout user and clear all auth data
   */
  /**
   * Lock screen - preserve user data for quick unlock
   */
  lock(): void {
    // Only clear session storage - keep localStorage for lock screen
    sessionStorage.removeItem(AUTH_SESSION_KEY);
  }

  /**
   * Full logout - clear everything and sign out from Firebase
   */
  async logout(): Promise<void> {
    try {
      const isAndroid = Capacitor.getPlatform() === 'android';
      const isNative = Capacitor.isNativePlatform();

      // Always clear ALL auth storage on logout - no conditional logic
      // The "rememberDevice" feature should only apply to auto-lock, not logout
      sessionStorage.removeItem(AUTH_SESSION_KEY);
      localStorage.removeItem(AUTH_SESSION_KEY);

      // Use native plugin on Android
      // FORCE WEB SDK: Consistent with login changes
      if (false && isAndroid && isNative) {
        await FirebaseAuthentication.signOut();
      } else if (isNativeFirebaseAuth()) {
        await nativeSignOut();
      } else {
        await firebaseSignOut(auth);
      }
    } catch (error) {
      console.error('Logout error:', error);
      // Force clear everything even on error
      sessionStorage.removeItem(AUTH_SESSION_KEY);
      localStorage.removeItem(AUTH_SESSION_KEY);
      throw error; // Re-throw so caller knows logout had issues
    }
  }

  /**
   * Get current authenticated user
   */
  getCurrentUser(): AuthUser | null {
    // Check sessionStorage first
    const sessionUser = sessionStorage.getItem(AUTH_SESSION_KEY);
    if (sessionUser) {
      return JSON.parse(sessionUser);
    }

    // Check localStorage
    const savedUser = localStorage.getItem(AUTH_SESSION_KEY);
    if (savedUser) {
      const user = JSON.parse(savedUser);
      // Restore to session
      sessionStorage.setItem(AUTH_SESSION_KEY, savedUser);
      return user;
    }

    return null;
  }

  /**
   * Get Firebase ID token for API calls
   */
  async getAccessToken(): Promise<string | null> {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        return null;
      }
      return await currentUser.getIdToken();
    } catch (error) {
      console.error('Error getting access token:', error);
      return null;
    }
  }

  /**
   * Update user data in storage
   */
  updateUser(user: AuthUser): void {
    sessionStorage.setItem(AUTH_SESSION_KEY, JSON.stringify(user));

    if (user.rememberDevice) {
      localStorage.setItem(AUTH_SESSION_KEY, JSON.stringify(user));
    }
  }

  /**
   * Verify session is still valid
   */
  async verifySession(): Promise<boolean> {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        return false;
      }

      // Refresh token to verify it's still valid
      await currentUser.getIdToken(true);
      return true;
    } catch (error) {
      console.error('Session verification error:', error);
      return false;
    }
  }

  /**
   * Verify PIN code
   */
  async verifyPin(pin: string): Promise<AuthUser | null> {
    const user = this.getCurrentUser();

    if (!user) {
      return null;
    }

    if (user.pin === pin) {
      return user;
    }

    return null;
  }

  /**
   * Update PIN code
   * UPDATED: Supports both old flat /users and new company-scoped /companies/{companyId}/users collections
   */
  async updatePin(newPin: string): Promise<boolean> {
    const user = this.getCurrentUser();

    if (!user || !auth.currentUser) {
      return false;
    }

    try {
      // Find the user document (could be in old or new location)
      let userRef = null;

      // First check tenant-scoped collections
      try {
        const companiesRef = collection(db, 'companies');
        const companiesSnapshot = await getDocs(companiesRef);

        for (const companyDoc of companiesSnapshot.docs) {
          const companyId = companyDoc.id;
          const tenantUserRef = doc(db, 'companies', companyId, 'users', auth.currentUser.uid);
          const tenantUserDoc = await getDoc(tenantUserRef);

          if (tenantUserDoc.exists()) {
            userRef = tenantUserRef;
            break;
          }
        }
      } catch (error) {
      }

      // Fallback to old flat collection
      if (!userRef) {
        userRef = doc(db, 'users', auth.currentUser.uid);
      }

      // Update in Firestore
      await updateDoc(userRef, {
        pin: newPin,
        updatedAt: new Date()
      });

      const updatedUser = {
        ...user,
        pin: newPin,
      };

      this.updateUser(updatedUser);
      return true;
    } catch (error) {
      console.error('Error updating PIN:', error);
      return false;
    }
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return auth.currentUser !== null;
  }

  /**
   * Check if user has specific role
   */
  hasRole(role: string): boolean {
    const user = this.getCurrentUser();
    return user?.role === role;
  }

  /**
   * Check if user is superadmin
   */
  isSuperAdmin(): boolean {
    const user = this.getCurrentUser();
    return user?.role === 'superadmin' || user?.isSuperAdmin === true;
  }

  /**
   * Check if user is manager
   */
  isManager(): boolean {
    const user = this.getCurrentUser();
    return user?.role === 'manager' || user?.role === 'superadmin';
  }

  /**
   * Update user profile in Firestore
   * UPDATED: Supports both old flat /users and new company-scoped /companies/{companyId}/users collections
   */
  async updateUserProfile(userId: string, updates: {
    name?: string;
    firstName?: string;
    lastName?: string;
    phoneNumber?: string;
    phone?: string;
    company?: string;
    companyLogo?: string;
    location?: string;
    profilePhoto?: string;
    preferences?: any;
    security?: any;
  }): Promise<void> {
    try {

      // Find the user document (could be in old or new location)
      let userRef = null;
      let userLocation = 'unknown';

      // First check tenant-scoped collections
      try {
        const companiesRef = collection(db, 'companies');
        const companiesSnapshot = await getDocs(companiesRef);

        for (const companyDoc of companiesSnapshot.docs) {
          const companyId = companyDoc.id;
          const tenantUserRef = doc(db, 'companies', companyId, 'users', userId);
          const tenantUserDoc = await getDoc(tenantUserRef);

          if (tenantUserDoc.exists()) {
            userRef = tenantUserRef;
            userLocation = `companies/${companyId}/users`;
            break;
          }
        }
      } catch (error) {
      }

      // Fallback to old flat collection
      if (!userRef) {
        userRef = doc(db, 'users', userId);
        userLocation = 'users (legacy)';
      }

      // Prepare update object - merge all fields
      const updateData: any = {
        updatedAt: new Date()
      };

      // Add simple fields
      if (updates.name !== undefined) updateData.name = updates.name;
      if (updates.firstName !== undefined) updateData.firstName = updates.firstName;
      if (updates.lastName !== undefined) updateData.lastName = updates.lastName;
      if (updates.phoneNumber !== undefined) updateData.phone = updates.phoneNumber;
      if (updates.phone !== undefined) updateData.phone = updates.phone;
      if (updates.company !== undefined) updateData.company = updates.company;
      if (updates.companyLogo !== undefined) updateData.companyLogo = updates.companyLogo;
      if (updates.location !== undefined) updateData.location = updates.location;
      if (updates.profilePhoto !== undefined) updateData.profilePhoto = updates.profilePhoto;

      // Add name if we have first/last (override name if provided)
      if (updates.firstName || updates.lastName) {
        updateData.name = `${updates.firstName || ''} ${updates.lastName || ''}`.trim();
      }

      // Add nested objects
      if (updates.preferences !== undefined) updateData.preferences = updates.preferences;

      // Handle security updates with new schema (security/bio)
      if (updates.security !== undefined) {
        // Handle biometric separately for security/bio schema
        if (updates.security.biometricEnabled !== undefined) {
          try {
            const bioRef = doc(userRef, 'security', 'bio');
            await setDoc(bioRef, {
              enabled: updates.security.biometricEnabled,
              updatedAt: new Date()
            }, { merge: true });

            // Remove from main update to enforce new schema
            const securityUpdate = { ...updates.security };
            delete securityUpdate.biometricEnabled;

            // Only add to main doc if there are other security fields left
            if (Object.keys(securityUpdate).length > 0) {
              updateData.security = securityUpdate;
            }
          } catch (e) {
            console.error('❌ Failed to update security/bio:', e);
            // Fallback: include in main doc if subcollection update fails
            updateData.security = updates.security;
          }
        } else {
          // No biometric update, just update security field normally
          updateData.security = updates.security;
        }
      }




      await updateDoc(userRef, updateData);


      // Update local session storage
      const currentUser = this.getCurrentUser();
      if (currentUser) {
        // Construct the new security object by merging current + updates
        // We use 'updates.security' (the input) not 'updateData.security' (the filtered payload)
        const newSecurity = {
          ...(currentUser.security || {}),
          ...(updates.security || {})
        };

        const updatedUser = {
          ...currentUser,
          name: updateData.name || currentUser.name,
          phone: updateData.phone || currentUser.phone,
          company: updateData.company || currentUser.company,
          companyLogo: updateData.companyLogo || currentUser.companyLogo,
          location: updateData.location || currentUser.location,
          profilePhoto: updateData.profilePhoto || currentUser.profilePhoto,
          preferences: updateData.preferences || currentUser.preferences,
          security: newSecurity,
        };
        this.updateUser(updatedUser);
      }
    } catch (error) {
      console.error('❌ Error updating user profile:', error);
      throw error;
    }
  }

  /**
   * Update company information in Firestore
   */
  async updateCompanyInfo(companyId: string, updates: {
    name?: string;
    address?: {
      street?: string;
      city?: string;
      state?: string;
      zip?: string;
    };
    contactEmail?: string;
    contactPhone?: string;
  }): Promise<void> {
    try {
      // Query companies by id field
      const companyDocRef = doc(db, 'companies', companyId);
      const companySnap = await getDoc(companyDocRef);

      if (companySnap.exists()) {
        // Update existing company document
        await updateDoc(companyDocRef, {
          ...updates,
          updatedAt: new Date()
        });
      } else {
        // Create new company document if it doesn't exist
        await setDoc(companyDocRef, {
          id: companyId,
          ...updates,
          createdAt: new Date(),
          updatedAt: new Date(),
          status: 'active'
        });
      }
    } catch (error) {
      console.error('Error updating company info:', error);
      throw error;
    }
  }

  /**
   * Enable biometric authentication for the current user
   */
  async enableBiometric(password: string): Promise<boolean> {
    const user = this.getCurrentUser();
    if (!user) return false;

    try {
      // Import dynamically to avoid circular dependencies if any
      const { biometricService } = await import('../security/biometric');
      const result = await biometricService.register(user.email, password);

      if (result.success) {
        // Update user profile to reflect biometric enabled
        await this.updateUserProfile(user.id, {
          security: {
            ...user.security,
            biometricEnabled: true
          }
        });
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error enabling biometric:', error);
      return false;
    }
  }

  /**
   * Login using biometric authentication
   */
  async loginWithBiometric(): Promise<AuthUser | null> {
    try {
      const { biometricService } = await import('../security/biometric');
      const result = await biometricService.authenticate();

      if (result.success && result.credentials) {
        const { username, password } = result.credentials;
        if (username && password) {
          return this.login(username, password);
        }
      }
      return null;
    } catch (error) {
      console.error('Biometric login error:', error);
      throw error;
    }
  }

  /**
   * Legacy method for backward compatibility
   */
  async authenticateWithBackend(
    emailOrPhone: string,
    password: string
  ): Promise<AuthUser | null> {
    return this.login(emailOrPhone, password);
  }
}

export const authService = new AuthService();
